import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { Dialog, DialogTitle, DialogContent, TableRow, TableCell, Typography } from '@mui/material'
import { Cancel } from '@mui/icons-material'
import { Link } from 'react-router-dom'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import SimpleTable from 'components/ui/SimpleTable'
import { ClientRoute } from 'routes/client'
import { selectGame } from 'store/game/slice'
import { deleteGamePlayer, getGame } from 'store/game/thunks'
import { selectLocale } from 'store/locale/slice'
import { convertHcpToString } from 'utils/convertHcpToString'

interface Props {
  open: boolean
  close: () => void
  round: GameSearchRound
  user?: User
}

export default function SingleRoundPlayersModal({ open, close, round, user }: Props): JSX.Element {
  const [currentRound, setCurrentRound] = useState<GameSearchRound | null>(null)
  const [currentGameId, setCurrentGameId] = useState<number | undefined>(undefined)
  const intl = useIntl()
  const dispatch = useDispatch()
  const locale = useSelector(selectLocale)
  const { deletingStatus, deletingId, status, game } = useSelector(selectGame)

  useEffect(() => {
    if (!currentRound) {
      setCurrentRound(round)
    } else if (currentRound?.roundId !== round.roundId) {
      close()
    }
  }, [round, currentRound, close])

  useEffect(() => {
    if (round.eventId && currentGameId !== round.eventId) {
      dispatch(getGame(round.eventId))
      setCurrentGameId(round.eventId)
    }
  }, [round, currentGameId, dispatch])

  const columns: TableColumn[] = [
    {
      id: 'id',
      label: intl.formatMessage({ id: 'strings.userId' }),
    },
    {
      id: 'name',
      label: intl.formatMessage({ id: 'strings.name' }),
    },
    {
      id: 'roundHcp',
      label: intl.formatMessage({ id: 'strings.roundHcp' }),
    },
    {
      id: 'teeBox',
      label: intl.formatMessage({ id: 'strings.teeBox' }),
    },
    {
      id: 'courseVersion',
      label: intl.formatMessage({ id: 'strings.courseVersion' }),
    },
    {
      id: 'remove',
      label: '',
    },
  ]

  const getPlayers = () => {
    if (!game) {
      return []
    }

    return game.players
  }

  const getGroupPlayer = (profileId: number | null): GroupPlayer | undefined => {
    return game?.groups?.flatMap((group) => group.players).find((player: GroupPlayer) => player?.userId === profileId)
  }

  const getTeeBoxByProfileId = (profileId: number | null): GameCourseTeeBox | undefined | null => {
    const teeBoxId = getGroupPlayer(profileId)?.teeboxId
    return game?.courses
      ?.flatMap((course: GameCourse) => course.teeboxes)
      .find((teeBox: GameCourseTeeBox | null | undefined) => teeBox?.id === teeBoxId)
  }

  const getCourseByProfileId = (profileId: number | null): GameCourse | undefined => {
    const courseId = getTeeBoxByProfileId(profileId)?.courseId
    return game?.courses?.find((course: GameCourse) => course.id === courseId)
  }

  const getRoundDate = () => {
    const date = round.date.date || round.date
    return new Intl.DateTimeFormat(locale.language.code).format(new Date(date))
  }

  const removePlayerFromGame = (player: GamePlayer) => {
    const confirmDeleteText = intl.formatMessage({ id: 'strings.deleteGamePlayerConfirmation' })
    if (round?.roundId && player.userId && window.confirm(confirmDeleteText)) {
      dispatch(
        deleteGamePlayer({
          gameId: round.eventId,
          playerId: player.userId,
        }),
      )
    }
  }

  const isDeletingThisPlayer = (player: GamePlayer) => {
    return deletingStatus === 'loading' && player.userId === deletingId
  }

  return (
    <StyledDialog open={open} onClose={close}>
      <DialogTitle id="form-dialog-title">
        {round?.name} <GameDate>{getRoundDate()}</GameDate>
        <CloseModal onClick={close} />
      </DialogTitle>
      <DialogContent>
        {status === 'succeeded' ? (
          <SimpleTable columns={columns}>
            {getPlayers().map((player: GamePlayer, index: number) => {
              return (
                <TableRow key={index}>
                  <TableCell>{player.userId}</TableCell>
                  <TableCell>
                    <PlayerLink
                      to={player.userId ? ClientRoute.ADMIN_USER(player.userId) : '#'}
                      onClick={close}
                      className={user && user.id === player.userId ? 'disabled' : ''}
                    >
                      {player.firstName} {player.lastName}
                    </PlayerLink>
                    {player.isTemporary && (
                      <Typography
                        sx={{
                          color: '#686868',
                          fontSize: 'small',
                          fontVariant: 'titling-caps',
                        }}
                      >
                        Unregistered
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{convertHcpToString(getGroupPlayer(player.userId)?.hcp || null)}</TableCell>
                  <TableCell>{getTeeBoxByProfileId(player.userId)?.teeboxName || null}</TableCell>
                  <TableCell>{getCourseByProfileId(player.userId)?.version || null}</TableCell>
                  <TableCell>
                    {isDeletingThisPlayer(player) ? (
                      <LoadingSpinnerGreen size={24} />
                    ) : (
                      <RemoveIcon onClick={() => removePlayerFromGame(player)} />
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </SimpleTable>
        ) : (
          <TableLoading>
            <LoadingSpinnerGreen size={24} />
          </TableLoading>
        )}
      </DialogContent>
    </StyledDialog>
  )
}

const TableLoading = styled.div`
  padding-left: 45%;
`

const StyledDialog = styled(Dialog)`
  & .MuiDialog-paper {
    min-width: 500px;
  }
`

const CloseModal = styled(Cancel)`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
`

const GameDate = styled.div`
  font-size: 0.75em;
`

const PlayerLink = styled(Link)`
  color: ${(props) => props.theme.palette.primary.main};

  &.disabled {
    color: ${(props) => props.theme.customPalette.darkGray};
    pointer-events: none;
    text-decoration: none;
  }
`

const RemoveIcon = styled(Cancel)`
  color: ${(props) => props.theme.customPalette.error};
  cursor: pointer;
  &:hover {
    opacity: 0.85;
  }
`
